import React, { Suspense } from 'react'
import { useMatchMedia, $historyPathname, lazyWithRetry } from '@rst/libs-ts'
import { useStore } from 'effector-react'

import { MainCtx, mainCtx } from './ctx'
import Main from './features/main/templates/main'
import { MainMobile } from './features/main/templates/main-mobile'
import { LanguageProps } from '../common/lang-selector'
import { useL10nCtx } from '../common/l10n-ctx'

const Header = lazyWithRetry(() => import('./features/headers'))

export const HeaderMain: React.FC<{
	ctx: MainCtx
	langProps: LanguageProps | null
	headerWrapper?: (children: React.ReactNode) => React.ReactNode
	mainWrapper?: (children: React.ReactNode) => React.ReactNode
}> = ({ ctx, headerWrapper, mainWrapper, langProps }) => {
	return (
		<mainCtx.Provider value={ctx}>
			<HeaderMainComponent
				headerWrapper={headerWrapper}
				mainWrapper={mainWrapper}
				langProps={langProps}
			/>
		</mainCtx.Provider>
	)
}

const HeaderMainComponent: React.FC<{
	headerWrapper?: (children: React.ReactNode) => React.ReactNode
	mainWrapper?: (children: React.ReactNode) => React.ReactNode
	langProps: LanguageProps | null
}> = ({ headerWrapper, mainWrapper, langProps }) => {
	const path = useStore($historyPathname)
	const sm = useMatchMedia(x => x.less.sm)

	const { availableLangs } = useL10nCtx()

	const isMainPage =
		path === '/' ||
		(/^\/\w\w(\/?)$/i.test(path) && availableLangs.includes(path.slice(1, 3)))

	if (!isMainPage)
		return (
			<Suspense fallback={() => null}>
				{headerWrapper ? (
					<>{headerWrapper(<Header langProps={langProps} />)}</>
				) : (
					<Header langProps={langProps} />
				)}
			</Suspense>
		)
	return sm ? (
		<MobileMainView langProps={langProps} />
	) : (
		<DeskMainView
			mainWrapper={mainWrapper}
			headerWrapper={headerWrapper}
			langProps={langProps}
		/>
	)
}

const MobileMainView = ({ langProps }: { langProps: LanguageProps | null }) => {
	return (
		<>
			<Suspense fallback={null}>
				<Header langProps={langProps} />
			</Suspense>
			<MainMobile />
		</>
	)
}
const DeskMainView: React.FC<{
	headerWrapper?: (children: React.ReactNode) => React.ReactNode
	mainWrapper?: (children: React.ReactNode) => React.ReactNode
	langProps: LanguageProps | null
}> = ({
	headerWrapper: HeaderWrapper,
	mainWrapper: MainWrapper,
	langProps,
}) => {
	const { head_pos } = useStore(mainCtx.useCtx().$head)

	return (
		<>
			<Suspense fallback={null}>
				{head_pos === 'top' ? (
					HeaderWrapper ? (
						HeaderWrapper(<Header langProps={langProps} />)
					) : (
						<Header langProps={langProps} />
					)
				) : null}
			</Suspense>
			{MainWrapper ? MainWrapper(<Main />) : <Main />}
			<Suspense fallback={null}>
				{head_pos === 'bottom' ? (
					HeaderWrapper ? (
						HeaderWrapper(<Header langProps={langProps} />)
					) : (
						<Header langProps={langProps} />
					)
				) : null}
			</Suspense>
		</>
	)
}
